import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { Seo } from "@/components/Seo";
import { StatusBadge } from "@/components/StatusBadge";
import { TheatreImage } from "@/components/TheatreImage";
import { ArrowLeft, ArrowRight, Users, Eye, Accessibility, MapPin, Loader2, Camera, Armchair } from "lucide-react";

const sameVenue = (a = "", b = "") => a.trim().toLowerCase() === b.trim().toLowerCase();
const paragraphs = (text) => (text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

export default function TheatrePage() {
  const { slug } = useParams();
  const [theatre, setTheatre] = useState(null);
  const [shows, setShows] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTheatre(null);
    setShows([]);
    api.get(`/theatre-info/slug/${slug}`)
      .then((r) => setTheatre(r.data))
      .catch(() => setTheatre(false));
  }, [slug]);

  useEffect(() => {
    if (!theatre) return;
    api.get("/shows")
      .then((r) =>
        setShows((r.data || []).filter((s) => sameVenue(s.venue, theatre.name) && s.status !== "Closed"))
      )
      .catch(() => setShows([]));
  }, [theatre]);

  useEffect(() => {
    if (!theatre) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "PerformingArtsTheater",
      name: theatre.name,
      description: theatre.description,
      url: `https://mytheatre.uk/theatres/${theatre.slug}`,
      ...(theatre.image_url ? { image: theatre.image_url } : {}),
    });
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [theatre]);

  if (theatre === null) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" />
      </div>
    );
  }

  if (theatre === false) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center">
        <Seo title="Theatre not found" noindex />
        <h1 className="font-serif text-3xl font-semibold">Theatre not found</h1>
        <p className="mt-3 text-muted-foreground">We couldn't find that theatre guide.</p>
        <Link to="/theatre-guide" className="mt-6 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-[#B82332]">
          Back to Theatre A-Z
        </Link>
      </div>
    );
  }

  const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${theatre.name}, London`)}`;

  return (
    <div>
      <Seo
        title={`${theatre.name} seating guide & accessibility`}
        description={`Where to sit at the ${theatre.name}: seating guide, restricted-view warnings, accessibility details and what's on.`}
        image={theatre.image_url}
      />

      <section className="relative overflow-hidden border-b border-amber-900/20">
        <div className="absolute inset-0 overflow-hidden">
          {theatre.image_url ? (
            <img src={theatre.image_url} alt="" aria-hidden="true" className="h-full w-full scale-125 object-cover opacity-60 blur-2xl" />
          ) : (
            <div className="h-full w-full bg-[#161118]" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/50 via-[#0D0A0E]/75 to-[#0D0A0E]" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <Link to="/theatre-guide" className="inline-flex items-center gap-1.5 text-sm text-amber-100/70 transition-colors hover:text-amber-50">
            <ArrowLeft className="h-4 w-4" /> Theatre A-Z
          </Link>

          <div className="mt-6 grid items-center gap-8 lg:grid-cols-2">
            <div>
              <p className="overline-label text-[#C89B3C]">London theatre guide</p>
              <h1 className="mt-2 font-serif text-4xl font-semibold text-amber-50 sm:text-5xl">{theatre.name}</h1>
              {theatre.capacity && (
                <div className="mt-3 flex items-center gap-2 text-amber-100/80">
                  <Users className="h-4 w-4" /> {theatre.capacity} seats
                </div>
              )}
              {theatre.description && <p className="mt-4 max-w-xl text-amber-100/75">{theatre.description}</p>}
              <a
                href={directions}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-100/30 px-5 py-2.5 text-sm font-medium text-amber-50 transition-colors hover:bg-white/10"
              >
                <MapPin className="h-4 w-4" /> Get directions
              </a>
            </div>

            <div>
              <TheatreImage
                src={theatre.image_url}
                alt={`${theatre.name} exterior`}
                className="aspect-[4/3] w-full rounded-2xl border border-amber-900/30"
              />
              {theatre.image_url && theatre.image_credit && (
                <a
                  href={theatre.image_credit}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-xs text-amber-100/50 hover:underline"
                >
                  Photo: Wikimedia Commons / Wikipedia
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[600px]" aria-hidden="true">
          {theatre.image_url && (
            <img src={theatre.image_url} alt="" className="h-full w-full scale-150 object-cover opacity-25 blur-3xl" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/0 via-[#0D0A0E]/70 to-[#0D0A0E]" />
        </div>
      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-3">
        <div className="space-y-10 lg:col-span-2">
          {theatre.seating_guide && (
                        <section className="rounded-2xl border border-[#C89B3C]/25 bg-gradient-to-br from-[#C89B3C]/10 to-[#161118]/70 p-6 backdrop-blur">
                        <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#C89B3C]/15 text-[#C89B3C]">
                            <Eye className="h-5 w-5" />
                          </span>
                          Seating guide
                        </h2>
                        {paragraphs(theatre.seating_guide).map((p, i) => (
                          <p key={i} className="mt-3 leading-7 text-amber-50/85">{p}</p>
                        ))}
                      </section>
          )}

{(theatre.seat_view_url || theatre.seatplan_url) && (
            <section className="rounded-2xl border border-[#C89B3C]/40 bg-gradient-to-br from-[#C89B3C]/20 to-[#161118]/70 p-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#C89B3C]/20 text-[#C89B3C]">
                  <Camera className="h-5 w-5" />
                </span>
                <h2 className="font-serif text-xl font-semibold leading-tight text-amber-50">
                  See the view from your seat
                </h2>
              </div>
              <p className="mt-3 text-sm leading-6 text-amber-50/80">
                Real photos from real seats at {theatre.name}, so you know what you will see before you book.
              </p>
              <a
                href={theatre.seat_view_url || theatre.seatplan_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-4 inline-flex w-full items-center justify-between gap-3 rounded-full bg-[#C89B3C] px-5 py-3 font-semibold text-black transition-transform hover:scale-[1.02] sm:w-auto sm:min-w-[18rem]"
              >
                Browse seat photos
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
              {theatre.seat_view_url && theatre.seatplan_url && (
                <div className="mt-3">
                  <a
                    href={theatre.seatplan_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[#C89B3C] hover:underline"
                  >
                    <Armchair className="h-4 w-4" /> Seating plan and reviews on SeatPlan
                  </a>
                </div>
              )}
              <p className="mt-3 text-xs text-amber-100/50">
                Photos from {theatre.seat_view_url ? "A View From My Seat" : "SeatPlan"}. Opens in a new tab.
              </p>
            </section>
          )}

          {theatre.accessibility && (
                        <section className="rounded-2xl border border-[#FF758F]/25 bg-gradient-to-br from-[#FF758F]/10 to-[#161118]/70 p-6 backdrop-blur">
                        <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF758F]/15 text-[#FF758F]">
                            <Accessibility className="h-5 w-5" />
                          </span>
                          Accessibility
                        </h2>
                        <p className="mt-3 leading-7 text-amber-50/85">{theatre.accessibility}</p>
                      </section>
          )}

<p className="rounded-xl border border-white/10 bg-[#161118]/70 p-4 text-sm text-muted-foreground backdrop-blur">
            These notes are compiled from public sources and the venue's own information, and sources sometimes
            disagree. Check your exact seat on the seller's seat map, and contact the theatre about access before you book.
          </p>
        </div>

        <aside>
        <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-amber-50">
            <span className="h-6 w-1 flex-shrink-0 rounded-full bg-[#B82332]" /> On now at {theatre.name}
          </h2>
          <div className="mt-4 space-y-3">
            {shows.length === 0 && (
              <p className="rounded-xl border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">
                No shows are listed here at the moment.{" "}
                <Link to="/shows" className="text-primary hover:underline">Browse all shows</Link>
              </p>
            )}
            {shows.map((s) => (
              <Link
                key={s.id}
                to={`/shows/${s.slug}`}
                                className="flex gap-3 rounded-xl border border-white/10 bg-[#161118]/70 p-3 backdrop-blur transition-colors hover:border-[#C89B3C]/60"
              >
                {s.image_url && (
                  <img src={s.image_url} alt={s.name} className="h-20 w-14 flex-shrink-0 rounded-lg object-cover" />
                )}
                <div className="min-w-0">
                  <p className="font-serif text-base font-semibold leading-snug">{s.name}</p>
                  <div className="mt-1.5"><StatusBadge status={s.status} testId={`theatre-status-${s.id}`} /></div>
                  {s.booking_until && (
                    <p className="mt-1.5 text-xs text-muted-foreground">Booking until {fmtDate(s.booking_until)}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
      </div>
    </div>
  );
}